import { useState } from "react";
import { X, ArrowRight, Trash2, PauseCircle } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

/**
 * Two-choice cancellation dialog shown when a paying user clicks
 * "Opzeggen" on the profile page.
 *
 *   A) Soft cancel — subscription keeps running until `subscription_expires_at`
 *      and then automatically drops to the Free Account. Data is preserved.
 *
 *   B) Hard delete — hands off to the existing full account-deletion modal
 *      (opens via the `onOpenDelete` callback).
 */
export default function CancelChoiceModal({ sub, onClose, onCancelled, onOpenDelete }) {
  const { t, lang } = useT();
  const [choice, setChoice] = useState(null); // null | "soft"
  const [reason, setReason] = useState("job_found");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const expiryIso = sub?.subscription_expires_at || sub?.trial_ends_at || null;
  const expiryDate = expiryIso
    ? new Date(expiryIso).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", {
        day: "numeric", month: "long", year: "numeric",
      })
    : "—";

  const confirmSoft = async () => {
    setBusy(true);
    try {
      await api.post("/billing/cancel", { reason, note });
      toast.success(t("ppMemCancelDone"));
      onCancelled?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admActionFailed"));
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="cancel-choice-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F8F6] max-w-2xl w-full border border-neutral-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-neutral-200">
          <div>
            <p className="ed-eyebrow">{t("cancelChoiceEyebrow")}</p>
            <h2 className="font-serif text-2xl mt-1">{t("cancelChoiceTitle")}</h2>
          </div>
          <button
            data-testid="cancel-choice-close"
            onClick={onClose}
            className="p-1 hover:bg-neutral-200 transition-colors"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        {choice === null && (
          <div className="p-6 space-y-3" data-testid="cancel-choice-picker">
            {/* Option A — soft cancel to Free Account */}
            <button
              data-testid="cancel-choice-soft"
              onClick={() => setChoice("soft")}
              className="w-full text-left flex items-start gap-4 p-5 border border-neutral-300 bg-white hover:border-neutral-900 hover:shadow-[3px_3px_0_0_rgba(0,0,0,0.08)] transition-all"
            >
              <PauseCircle size={22} strokeWidth={1.5} className="mt-0.5 shrink-0 text-neutral-700" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg leading-tight">{t("cancelChoiceSoftTitle")}</p>
                <p className="text-sm text-neutral-600 mt-1.5 leading-relaxed">
                  {t("cancelChoiceSoftBody", { date: expiryDate })}
                </p>
              </div>
              <ArrowRight size={16} strokeWidth={1.5} className="mt-1 shrink-0 text-neutral-400" />
            </button>

            {/* Option B — hard account deletion */}
            <button
              data-testid="cancel-choice-hard"
              onClick={() => { onOpenDelete?.(); onClose?.(); }}
              className="w-full text-left flex items-start gap-4 p-5 border border-rose-300 bg-white hover:border-rose-700 hover:shadow-[3px_3px_0_0_rgba(190,18,60,0.12)] transition-all"
            >
              <Trash2 size={22} strokeWidth={1.5} className="mt-0.5 shrink-0 text-rose-700" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg leading-tight text-rose-800">{t("cancelChoiceHardTitle")}</p>
                <p className="text-sm text-neutral-600 mt-1.5 leading-relaxed">
                  {t("cancelChoiceHardBody")}
                </p>
              </div>
              <ArrowRight size={16} strokeWidth={1.5} className="mt-1 shrink-0 text-rose-400" />
            </button>
          </div>
        )}

        {choice === "soft" && (
          <div className="p-6" data-testid="cancel-choice-soft-form">
            <p className="text-sm text-neutral-700 leading-relaxed">
              {t("cancelChoiceReasonIntro")}
            </p>
            <div className="mt-4 space-y-2">
              {["job_found", "too_expensive", "not_useful", "temporary_break", "other"].map((r) => (
                <label key={r} className="flex items-center gap-3 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="reason"
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    data-testid={`cancel-reason-${r}`}
                    className="accent-neutral-900"
                  />
                  {t(`cancelReason_${r}`)}
                </label>
              ))}
            </div>
            <textarea
              data-testid="cancel-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("cancelChoiceNotePlaceholder")}
              rows={3}
              className="w-full mt-4 p-3 border border-neutral-300 bg-white text-sm font-serif focus:border-neutral-900 focus:outline-none resize-y"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button
                data-testid="cancel-choice-back"
                onClick={() => setChoice(null)}
                disabled={busy}
                className="ed-btn ed-btn-outline"
              >
                {t("common_back")}
              </button>
              <button
                data-testid="cancel-choice-confirm"
                onClick={confirmSoft}
                disabled={busy}
                className="ed-btn"
              >
                {busy ? "…" : t("cancelChoiceConfirmSoft")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
